import { FC } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import FormField from '@amzn/awsui-components-core/form-field';
import Textarea from '@amzn/awsui-components-core/textarea';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { CampaignType } from 'src/@types/valueMatching/enums';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';
import { isFieldModifiable } from '../../editability';

/** ASIN limits per campaign type */
export const ASIN_LIMITS: Record<CampaignType, number> = {
    [CampaignType.NO_COST_EMI]: 800,
    [CampaignType.CASHBACK]: 800,
    [CampaignType.BUYBACK]: 2000,
    [CampaignType.DEAL]: 4000,
    [CampaignType.INSTANT_BANK_DISCOUNT]: 2000,
};

const DEFAULT_ASIN_LIMIT = 800;

export interface AsinInclusionSectionProps extends SectionBaseProps {
    /** Current comma-separated ASIN string */
    asins: string;
    /** Callback to update the ASIN string */
    setAsins: (value: string) => void;
    /** Current campaign type (used to determine ASIN limit) */
    campaignType: CampaignType | undefined;
    /** Inline validation error message for the ASIN list field */
    errorText?: string;
    /** Suspended (excluded) product ASINs — shown as read-only below the included products */
    suspendedAsins?: string[];
    /** Excluded old product item IDs (Buyback only) — editable textarea */
    excludedOldItemIds?: string;
    /** Callback to update excluded old item IDs */
    setExcludedOldItemIds?: (value: string) => void;
}

/**
 * ASIN inclusion section — renders a textarea for comma-separated ASINs.
 * The placeholder shows the ASIN limit which varies by campaign type:
 * 800 (NCEMI/CB), 2000 (BB), 4000 (DEAL).
 * Owns its own i18n bundle (`organisms.asinInclusionSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const AsinInclusionSection: FC<AsinInclusionSectionProps> = ({
    asins,
    setAsins,
    campaignType,
    mode,
    disabled,
    errorText,
    suspendedAsins,
    excludedOldItemIds,
    setExcludedOldItemIds,
}) => {
    const { translate } = useTranslate('organisms.asinInclusionSection');
    // Per-campaign-type editability lives in `editability.ts` so adding a new
    // type (Coupons, IBD, …) is a single-line config change, not an edit here.
    const isDisabled = disabled
        ?? (mode === 'view' || (mode === 'modify' && !isFieldModifiable(campaignType, 'asins')));
    const asinLimit = campaignType ? ASIN_LIMITS[campaignType] : DEFAULT_ASIN_LIMIT;
    const hasSuspendedAsins = suspendedAsins && suspendedAsins.length > 0;
    const showExcludedOldItems = excludedOldItemIds != null;

    return (
        <Container
            header={<Header variant="h2">{translate('section_asin_inclusion')}</Header>}
        >
            <SpaceBetween size="l">
                <FormField
                    label={translate('asin_inclusion_label')}
                    description={mode !== 'view' ? translate('asin_inclusion_description', { limit: asinLimit }) : undefined}
                    errorText={errorText}
                    i18nStrings={{ errorIconAriaLabel: translate('error_icon_aria_label') }}
                >
                    <Textarea
                        value={asins}
                        onChange={({ detail }) => setAsins(detail.value)}
                        placeholder={translate('asin_inclusion_placeholder', { limit: asinLimit })}
                        rows={mode === 'view' ? 2 : 4}
                        ariaRequired={true}
                        disabled={isDisabled}
                        data-testid="product-participation-criteria"
                        invalid={!!errorText}
                    />
                </FormField>
                {showExcludedOldItems && (
                    <FormField label={translate('excluded_old_item_ids_label')}>
                        <Textarea
                            value={excludedOldItemIds}
                            onChange={({ detail }) => setExcludedOldItemIds?.(detail.value)}
                            placeholder={translate('excluded_old_item_ids_placeholder')}
                            rows={mode === 'view' ? 2 : 4}
                            ariaRequired={false}
                            disabled={isDisabled}
                            data-testid="product-exclusion-criteria"
                        />
                    </FormField>
                )}
                {hasSuspendedAsins && (
                    <FormField label={translate('excluded_products_label')}>
                        <Textarea
                            value={suspendedAsins.join(', ')}
                            onChange={() => {}}
                            rows={2}
                            disabled={true}
                            ariaRequired={false}
                            data-testid="suspended-products-section"
                        />
                    </FormField>
                )}
            </SpaceBetween>
        </Container>
    );
};

export default AsinInclusionSection;
